import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang, L } from "@/context/LangContext";
import { ROLE_LABEL } from "@/lib/format";
import {
  LayoutDashboard, Users, Wallet, ReceiptText, FileText, Coins, Clock, PieChart,
  LogOut, Menu, X, Target, CalendarDays, Timer, CalendarCheck, BarChart3, FileSpreadsheet,
  Receipt, CalendarRange, Sun, ChevronDown, Upload, Banknote, UserCog, Landmark, Sparkles, ShieldCheck, Archive, BadgeCheck, TrendingUp, Table2, Cog,
  ChefHat, Carrot, Truck, Scale, ClipboardList, Waves, Gauge, Wrench, Gavel, ShoppingCart, Plug, PartyPopper,
  ClipboardCheck, Home, ListChecks, ArrowLeft, Languages, BedDouble, Package, PlusCircle, LayoutGrid,
  Database,
} from "lucide-react";
import { TafLockup } from "@/components/Brand";
import { companyMeta } from "@/lib/company";
import NotificationsBell from "@/components/NotificationsBell";

const I = (to, el, en, icon) => ({ to, label: { el, en }, icon });

export const STAFF_ROLES = ["concierge", "housekeeping", "fnb", "maintenance", "gardener", "employee"];

const G_TODAY = { key: "today", label: { el: "Σήμερα", en: "Today" }, icon: LayoutDashboard, items: [
  I("/", "Πίνακας Ελέγχου", "Dashboard", LayoutDashboard),
  I("/field-ops/housekeeping", "Εργασίες Housekeeping", "Housekeeping Tasks", ClipboardCheck),
  I("/maintenance/my", "Βλάβες & Εργασίες Συντήρησης", "Issues & Maintenance Tasks", Wrench),
  I("/maintenance-tasks", "Πλάνο Συντήρησης & Winter", "Maintenance Plan & Winter", ListChecks),
  I("/field-ops/issues", "Αναφορές Βλαβών (Housekeeping)", "Issue Reports (Housekeeping)", ClipboardList),
  I("/readings", "Μετρήσεις Πισίνες · Νερό · Ρεύμα", "Readings Pools · Water · Power", Gauge),
  I("/field-ops/kpis", "KPIs Πεδίου", "Field KPIs", BarChart3),
  I("/field-ops/settings", "Μονάδες & Checklists", "Units & Checklists", Home),
] };
const G_REVENUE = { key: "revenue", label: { el: "Κρατήσεις & Έσοδα", en: "Bookings & Revenue" }, icon: BedDouble, items: [
  I("/reservations", "Κρατήσεις (Hotelizer)", "Reservations (Hotelizer)", BedDouble),
  I("/villa-plan", "Πλάνο Βιλών", "Villa Plan", CalendarRange),
  I("/pace", "Pace Κρατήσεων", "Booking Pace", TrendingUp),
  I("/events", "Εκδηλώσεις", "Events", PartyPopper),
  I("/income", "Έσοδα ανά Μήνα", "Monthly Income", Coins),
  I("/income-budget", "Έσοδα: Budget vs Πραγματικά", "Income: Budget vs Actual", Target),
  I("/integrations", "Σύνδεση PMS / Hotelizer", "PMS / Hotelizer Connection", Plug),
] };
const G_EXPENSES = { key: "expenses", label: { el: "Έξοδα & Αγορές", en: "Expenses & Purchases" }, icon: Receipt, subgroups: [
  { key: "exp", label: { el: "Έξοδα", en: "Expenses" }, items: [
    I("/expenses", "Αποδείξεις Προσωπικού", "Staff Receipts", ReceiptText),
    I("/invoices", "Παραστατικά Εξόδων", "Expense Invoices", Receipt),
    I("/supplier-expenses", "Έξοδα Προμηθευτών", "Supplier Expenses", Table2),
    I("/expense-budget", "Έξοδα: Budget vs Πραγματικά", "Expenses: Budget vs Actual", Target),
  ] },
  { key: "purch", label: { el: "Αγορές", en: "Purchases" }, items: [
    I("/purchase-pivot", "Ανάλυση Αγορών (Pivot)", "Purchase Pivot", Table2),
    I("/vendors", "Προμηθευτές", "Suppliers", Truck),
    I("/catalog", "Κατάλογος Ειδών", "Product Catalogue", Package),
    I("/price-compare", "Σύγκριση Τιμών & Διαγωνισμοί", "Price Comparison & Bidding", Scale),
    I("/orders", "Παραγγελίες Προμηθευτών", "Purchase Orders", ClipboardList),
    I("/supply-order", "Λίστα Αναγκών Υλικών", "Supplies Needs List", ShoppingCart),
  ] },
  { key: "stock", label: { el: "Απογραφές (Stock Counting)", en: "Stock Counting" }, items: [
    I("/stock-count?dept=fnb", "Απογραφή F&B", "Stock Count F&B", Scale),
    I("/stock-count?dept=housekeeping", "Απογραφή Housekeeping", "Stock Count Housekeeping", Scale),
    I("/stock-count?dept=maintenance", "Απογραφή Συντήρησης", "Stock Count Maintenance", Scale),
  ] },
  { key: "fnb", label: { el: "F&B", en: "F&B" }, items: [
    I("/ingredients", "Υλικά & Τιμές Κουζίνας", "Kitchen Ingredients & Prices", Carrot),
    I("/recipes", "Συνταγές & Κοστολόγηση", "Recipes & Costing", ChefHat),
  ] },
] };
const G_RESULTS = { key: "results", label: { el: "Οικονομικά", en: "Finance" }, icon: BarChart3, items: [
  I("/ledger", "Ενιαίο Καθολικό (όλες οι πηγές)", "Unified Ledger (all sources)", Database),
  I("/pl-report", "Αποτελέσματα P&L", "P&L Results", FileSpreadsheet),
  I("/pl-budget", "Προϋπολογισμός P&L", "P&L Budget", BarChart3),
  I("/pl-variance", "Πραγματικά vs Προϋπολογισμός", "Actual vs Budget", TrendingUp),
  I("/business-stats", "Στατιστικά & KPIs Επιχείρησης", "Business Statistics & KPIs", PieChart),
  I("/cashflow", "Ταμειακές Ροές", "Cash Flow", Wallet),
  I("/loans", "Δάνεια", "Loans", Landmark),
  I("/cheques", "Επιταγές", "Cheques", FileText),
  I("/finance-categories", "Κατηγορίες Εσόδων & Εξόδων", "Income & Expense Categories", ListChecks),
] };
const G_TEAM = { key: "team", label: { el: "Ομάδα & Μισθοδοσία", en: "Team & Payroll" }, icon: Users, subgroups: [
  { key: "sched", label: { el: "Πρόγραμμα", en: "Schedule" }, items: [
    I("/team/settings", "Εργαζόμενοι, Τμήματα & Αργίες", "Employees, Departments & Holidays", Users),
    I("/schedule", "Πρόγραμμα Βαρδιών", "Shift Schedule", CalendarDays),
    I("/day-roster", "Βάρδια Ημέρας", "Daily Roster", Sun),
    I("/team/timesheets", "Φύλλα Ωρών", "Timesheets", Timer),
    I("/team/requests", "Αιτήματα Προγράμματος", "Schedule Requests", BadgeCheck),
    I("/leave", "Άδειες", "Leave", CalendarCheck),
    I("/team/timeclock", "Ωρολόγιο Kiosk", "Time Clock Kiosk", Clock),
  ] },
  { key: "pay", label: { el: "Μισθοδοσία", en: "Payroll" }, items: [
    I("/payroll", "Μισθοδοσία", "Payroll", Wallet),
    I("/budget", "Προϋπολογισμός Μισθών", "Payroll Budget", Target),
    I("/report", "Ετήσια Αναφορά Μισθοδοσίας", "Annual Payroll Report", PieChart),
    I("/tips", "Διανομή Tips", "Tips Distribution", Coins),
    I("/payslips", "Αποδείξεις Πληρωμής", "Payslips", FileText),
  ] },
] };
const G_SETTINGS = { key: "settings", label: { el: "Ανέβασμα & Ρυθμίσεις", en: "Uploads & Settings" }, icon: Cog, items: [
  I("/imports", "Κέντρο Εισαγωγών", "Import Center", Upload),
  I("/settings", "Κατηγορίες & Γενικές Ρυθμίσεις", "Categories & General Settings", Cog),
  I("/users", "Χρήστες, Κάδος & Ιστορικό", "Users, Recycle Bin & History", ShieldCheck),
  I("/team/permissions", "Δικαιώματα", "Permissions", ShieldCheck),
] };

export const GROUPS = [G_TODAY, G_REVENUE, G_EXPENSES, G_RESULTS, G_TEAM, G_SETTINGS];

export const MANAGER_GROUPS = [
  { key: "today", label: { el: "Σήμερα", en: "Today" }, icon: LayoutDashboard, items: [
    I("/", "Πίνακας Ελέγχου", "Dashboard", LayoutDashboard), I("/field-ops/housekeeping", "Εργασίες Housekeeping", "Housekeeping Tasks", ClipboardCheck),
    I("/readings", "Μετρήσεις Πισίνες · Νερό · Ρεύμα", "Readings", Gauge), I("/villa-plan", "Πλάνο Βιλών", "Villa Plan", CalendarRange)] },
  { key: "team", label: { el: "Ομάδα & Βάρδιες", en: "Team & Shifts" }, icon: Users, items: [
    I("/schedule", "Πρόγραμμα Βαρδιών", "Shift Schedule", CalendarDays), I("/day-roster", "Βάρδια Ημέρας", "Daily Roster", Sun),
    I("/team/timesheets", "Φύλλα Ωρών", "Timesheets", Timer), I("/team/requests", "Αιτήματα Προγράμματος", "Schedule Requests", BadgeCheck),
    I("/leave", "Άδειες", "Leave", CalendarCheck), I("/team/settings", "Εργαζόμενοι, Τμήματα & Αργίες", "Employees & Departments", Users),
    I("/team/timeclock", "Ωρολόγιο Kiosk", "Time Clock Kiosk", Clock)] },
  { key: "tasks", label: { el: "Εργασίες & Βλάβες", en: "Tasks & Issues" }, icon: Wrench, items: [
    I("/maintenance/my", "Βλάβες & Εργασίες Συντήρησης", "Issues & Maintenance", Wrench), I("/maintenance-tasks", "Πλάνο Συντήρησης & Winter", "Maintenance Plan", ListChecks),
    I("/field-ops/issues", "Αναφορές Βλαβών (Housekeeping)", "Issue Reports", ClipboardList), I("/field-ops/kpis", "KPIs Πεδίου", "Field KPIs", BarChart3),
    I("/field-ops/settings", "Μονάδες & Checklists", "Units & Checklists", Home)] },
  { key: "stock", label: { el: "Αποθήκη & Παραγγελίες", en: "Stock & Orders" }, icon: Package, items: [
    I("/stock-count?dept=fnb", "Απογραφή F&B", "Stock Count F&B", Scale), I("/stock-count?dept=housekeeping", "Απογραφή Housekeeping", "Stock Count Housekeeping", Scale),
    I("/stock-count?dept=maintenance", "Απογραφή Συντήρησης", "Stock Count Maintenance", Scale), I("/supply-order", "Λίστα Αναγκών Υλικών", "Supplies Needs List", ShoppingCart),
    I("/orders", "Παραγγελίες Προμηθευτών", "Purchase Orders", ClipboardList), I("/vendors", "Προμηθευτές", "Suppliers", Truck),
    I("/catalog", "Κατάλογος Ειδών", "Product Catalogue", Package), I("/price-compare", "Σύγκριση Τιμών & Διαγωνισμοί", "Price Comparison", Scale)] },
];

export const groupsForRole = (role) => (STAFF_ROLES.includes(role) ? [] : role === "manager" ? MANAGER_GROUPS : GROUPS);

const ROLE_BADGE = {
  owner: "bg-[#B9AE9A]/20 text-[#6F6A60]",
  hr: "bg-[#6E7A63]/15 text-[#6E7A63]",
  manager: "bg-[#C86235]/15 text-[#a24a24]",
  concierge: "bg-[#8A7DAE]/15 text-[#5b4e82]",
  housekeeping: "bg-[#C98BA0]/15 text-[#8a4e63]",
  fnb: "bg-[#C8A24A]/15 text-[#8a6a1f]",
  maintenance: "bg-[#2E6F7A]/15 text-[#1f5560]",
  employee: "bg-[#45423C]/10 text-[#45423C]",
  gardener: "bg-[#6E7A63]/15 text-[#4A5A3E]",
  supplier: "bg-[#B9AE9A]/20 text-[#6F6A60]",
};

export const canSeeItem = (user) => (i) => (user?.permissions?.[i.to.split("?")[0]] ?? "-") !== "-";

export default function Layout({ children }) {
  const { user, logout, company, switchCompany } = useAuth();
  const { lang, toggle: toggleLang } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const t = (x) => L(x, lang);

  const canSee = canSeeItem(user);
  const isStaff = STAFF_ROLES.includes(user?.role);
  const showAi = !isStaff && (user?.permissions?.["/assistant"] ?? "-") !== "-";
  const groups = groupsForRole(user?.role)
    .map((g) => {
      if (g.subgroups) {
        const subs = g.subgroups.map((s) => ({ ...s, items: s.items.filter(canSee) })).filter((s) => s.items.length);
        return { ...g, subgroups: subs };
      }
      return { ...g, items: g.items.filter(canSee) };
    })
    .filter((g) => (g.subgroups ? g.subgroups.length : g.items.length));

  const itemsOf = (g) => (g.subgroups ? g.subgroups.flatMap((s) => s.items) : g.items);
  const activeGroup = groups.find((g) => itemsOf(g).some((i) => i.to === pathname))?.key || groups[0]?.key;
  const activeSub = groups.flatMap((g) => g.subgroups || []).find((s) => s.items.some((i) => i.to === pathname))?.key;
  const [openGroups, setOpenGroups] = useState({ [activeGroup]: true, ...(activeSub ? { [activeSub]: true } : {}) });
  const isOpen = (key) => openGroups[key] ?? key === activeGroup;
  const toggle = (key) => setOpenGroups((p) => ({ ...p, [key]: !isOpen(key) }));

  const renderItem = (it) => {
    const Icon = it.icon;
    return (
      <NavLink key={it.to} to={it.to} end={it.to === "/" || it.to === "/field-ops" || it.to === "/team"} onClick={() => setOpen(false)}
        data-testid={`nav-${it.to}`}
        className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${
          isActive ? "bg-[#45423C] text-white font-semibold shadow-sm" : "text-[#6F6A60] hover:bg-[#F1EDE6] hover:text-[#45423C]"}`}>
        <Icon className="h-3.5 w-3.5" /> {t(it.label)}
      </NavLink>
    );
  };

  const SideContent = () => (
    <>
      <div className="px-5 py-6 border-b border-[#E3DED4]">
        <TafLockup subtitle={lang === "el" ? "Kos Island · Διοίκηση" : "Kos Island · Administration"} light={false} />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {groups.map((g, gi) => {
          const GIcon = g.icon;
          const expanded = isOpen(g.key);
          const single = g.items && g.items.length === 1 && g.key === "overview";
          if (single) {
            const it = g.items[0];
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to} end onClick={() => setOpen(false)} data-testid={`nav-${it.to}`}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive ? "bg-[#45423C] text-white font-semibold shadow-sm" : "text-[#6F6A60] hover:bg-[#F1EDE6] hover:text-[#45423C]"}`}>
                <Icon className="h-4 w-4" /> {t(it.label)}
              </NavLink>
            );
          }
          return (
            <div key={g.key} className={gi ? "pt-1" : ""}>
              <button data-testid={`nav-group-${g.key}`} onClick={() => toggle(g.key)}
                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-[11px] uppercase tracking-wider font-semibold text-[#8A8577] hover:bg-[#F1EDE6] transition-colors">
                <span className="flex items-center gap-2.5"><GIcon className="h-3.5 w-3.5" /> {t(g.label)}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "" : "-rotate-90"}`} />
              </button>
              {expanded && g.subgroups && (
                <div className="mt-0.5 space-y-0.5 pl-3 border-l border-[#E3DED4] ml-4">
                  {g.subgroups.map((s) => {
                    const SIcon = s.icon;
                    const sExpanded = isOpen(s.key);
                    return (
                      <div key={s.key}>
                        <button data-testid={`nav-group-${s.key}`} onClick={() => toggle(s.key)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[11px] uppercase tracking-wider font-semibold text-[#9a968c] hover:bg-[#F1EDE6] transition-colors">
                          <span className="flex items-center gap-2"><SIcon className="h-3.5 w-3.5" /> {t(s.label)}</span>
                          <ChevronDown className={`h-3 w-3 transition-transform ${sExpanded ? "" : "-rotate-90"}`} />
                        </button>
                        {sExpanded && (
                          <div className="mt-0.5 space-y-0.5 pl-3 border-l border-[#E3DED4] ml-3">
                            {s.items.map((it) => renderItem(it))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {expanded && g.items && (
                <div className="mt-0.5 space-y-0.5 pl-3 border-l border-[#E3DED4] ml-4">
                  {g.items.map((it) => renderItem(it))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="p-4 border-t border-[#E3DED4]">
        <button onClick={logout} data-testid="logout-button"
          className="flex w-full items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B] transition-colors">
          <LogOut className="h-4 w-4" /> {lang === "el" ? "Αποσύνδεση" : "Logout"}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F7F5F1] text-[#3A3833]">

      <div>
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 sm:px-8 h-16 bg-[#F7F5F1]/85 backdrop-blur-md border-b border-[#E3DED4] no-print">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} data-testid="menu-home-button"
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-3 py-1.5 text-sm font-semibold hover:bg-[#56524A]">
              {isStaff ? <Home className="h-4 w-4" /> : <LayoutGrid className="h-4 w-4" />} <span className={isStaff ? "" : "hidden sm:inline"}>{isStaff ? (lang === "el" ? "Αρχική" : "Home") : (lang === "el" ? "Μενού" : "Menu")}</span>
            </button>
            {pathname !== "/" && (
              <button onClick={() => navigate(-1)} data-testid="back-button"
                className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] bg-white px-3 py-1.5 text-sm font-medium text-[#45423C] hover:border-[#B9AE9A] hover:bg-[#F1EDE6] transition-colors">
                <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">{lang === "el" ? "Πίσω" : "Back"}</span>
              </button>
            )}
            {!isStaff && <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#6F6A60] hidden sm:block" data-testid="header-company">
              {companyMeta(company).short} {lang === "el" ? "Διοίκηση" : "Administration"}
            </span>}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleLang} data-testid="lang-toggle" title={lang === "el" ? "Switch to English" : "Αλλαγή σε Ελληνικά"}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E3DED4] bg-white px-3 py-1.5 text-xs font-semibold text-[#45423C] hover:border-[#B9AE9A] hover:bg-[#F1EDE6] transition-colors">
              <Languages className="h-4 w-4" /> {lang === "el" ? "EL" : "EN"}
            </button>
            <NotificationsBell />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-none">{user?.name}</div>
              <div className="text-xs text-[#6F6A60]">{user?.email}</div>
            </div>
            <span className={`hidden sm:inline px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${ROLE_BADGE[user?.role]}`}>
              {ROLE_LABEL[user?.role] || user?.role}
            </span>
            <div className="hidden sm:flex h-9 w-9 rounded-full bg-[#45423C] text-white items-center justify-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <button onClick={logout} data-testid="logout-button" title={lang === "el" ? "Αποσύνδεση" : "Logout"}
              className="p-2 rounded-xl text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B]"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className={`px-4 sm:px-8 py-6 sm:py-8 w-full mx-auto max-w-[1400px] [&:has([data-wide])]:max-w-[2000px] ${isStaff ? "text-[18px]" : ""}`}>{children}</main>
        {showAi && pathname !== "/assistant" && (
          <button onClick={() => navigate("/assistant")} data-testid="ai-assistant-fab" title={lang === "el" ? "AI Βοηθός" : "AI Assistant"}
            className="fixed bottom-4 left-4 z-40 inline-flex items-center gap-2 rounded-full bg-[#45423C] text-white pl-3 pr-4 py-2 text-xs font-semibold shadow-lg hover:bg-[#56524A] transition-colors no-print">
            <Sparkles className="h-4 w-4 text-[#C8A24A]" /> {lang === "el" ? "AI Βοηθός" : "AI Assistant"}
          </button>
        )}
      </div>
    </div>
  );
}
